import { useCopyToClipboard } from 'usehooks-ts';

import { parseValueForFilter } from '@/explore/edit-pipeline/utils/filter';
import { createEmptyFilterCondition } from '@/explore/edit-pipeline/utils/filter';
import { createGroupingFromField } from '@/explore/utils/grouping';
import { Icon } from '@/components/icon';
import { Badge } from '@/components/badge';
import { AddToastFn } from '@/components/toast';
import { ExplorationUIContextValue } from '@/explore/exploration/exploration-ui-context';
import { formatPropertyValue } from '@/explore/utils/format';
import { DropdownIconSizePx } from '@/components/dropdown';

import { DataTableProperty } from '../types';

import { badgeColor, badgeBackground } from './context-menu.module.scss';

export const buildOpenDetailViewMenuItem = (
  property: DataTableProperty,
  link: string,
  closeEditor: () => void,
  scrollToCell: (cellId: string | null) => void,
) => ({
  label: (
    <span>
      Open{' '}
      <Badge
        label={property.relation?.name ?? 'Model'}
        bgColor={badgeBackground}
        textColor={badgeColor}
      />
    </span>
  ),
  icon: <Icon name="FileText" size={DropdownIconSizePx} />,
  onClick: () => {
    closeEditor();
    scrollToCell(null);
  },
  href: link,
  type: 'link' as const,
});

export const buildCopyValueMenuItem = (
  property: DataTableProperty,
  value: unknown,
  timezone: string,
  addToast: AddToastFn,
  copyToClipboard: ReturnType<typeof useCopyToClipboard>[1],
) => ({
  label: 'Copy Value',
  icon: <Icon name="Copy" size={DropdownIconSizePx} />,
  onClick: () => {
    copyToClipboard(
      formatPropertyValue(value, {
        type: property.type,
        format: 'raw',
        timezone,
      }),
    );
    addToast({
      title: 'Value copied!',
      content: () => 'Value copied to clipboard',
      kind: 'success',
    });
  },
});

export const buildLinkMenuItem = (value: unknown) => ({
  label: 'Open URL',
  icon: <Icon name="ExternalLink" size={DropdownIconSizePx} />,
  href: String(value),
  external: true,
  type: 'link' as const,
});

export const buildFilterMenuItem = (
  cellId: string,
  openEditor: (cellId: string) => void,
  openAddForm: ExplorationUIContextValue['openAddForm'],
  property: DataTableProperty,
  value?: unknown,
  timezone?: string,
) => ({
  label: 'Filter by',
  icon: <Icon name="Filter" size={DropdownIconSizePx} />,
  onClick: () => {
    openEditor(cellId);
    openAddForm({
      index: -1,
      formId: 'addFilter',
      parameters:
        value === null
          ? { key: property.key, operator: 'isnull' }
          : createEmptyFilterCondition(
              property,
              parseValueForFilter(value, property.type, timezone ?? 'UTC'),
            ),
    });
  },
});

export const buildGroupByMenuItem = (
  cellId: string,
  openEditor: (cellId: string) => void,
  openAddForm: ExplorationUIContextValue['openAddForm'],
  property: DataTableProperty,
) => ({
  label: 'Group by',
  icon: <Icon name="Summarise" size={DropdownIconSizePx} />,
  onClick: () => {
    openEditor(cellId);
    openAddForm({
      index: -1,
      formId: 'addGroupAggregate',
      parameters: {
        groups: [createGroupingFromField(property)],
        aggregations: [],
      },
    });
  },
});

export const buildHideColumnMenuItem = (
  property: DataTableProperty,
  hideColumn: (key: string) => void,
  disabled?: boolean,
) => ({
  label: 'Hide column',
  icon: <Icon name="EyeOff" size={DropdownIconSizePx} />,
  onClick: () => {
    hideColumn(property.key);
  },
  disabled,
});
